import { useState, useEffect, useCallback } from 'react';
import { Todo } from '@/types/todo';
import { shouldTriggerPaywall, getPaywallState } from '@/utils/paywallHelpers';

export function usePaywallChecker(todos: Todo[]) {
  const [isPaywallActive, setIsPaywallActive] = useState(false);
  const [lastCheck, setLastCheck] = useState(Date.now());

  const checkPaywall = useCallback(() => {
    const shouldTrigger = shouldTriggerPaywall(todos);
    setIsPaywallActive(shouldTrigger);
    setLastCheck(Date.now());
  }, [todos]);

  // Initial check
  useEffect(() => {
    checkPaywall();
  }, [checkPaywall]);

  // Check every 15 seconds for more responsive paywall triggering
  useEffect(() => {
    const interval = setInterval(checkPaywall, 15000);
    return () => clearInterval(interval);
  }, [checkPaywall]);

  // Check when todos change
  useEffect(() => {
    checkPaywall();
  }, [todos, checkPaywall]);

  // Listen for paywall state changes
  useEffect(() => {
    const handlePaywallStateChange = () => {
      checkPaywall();
    };
    
    // Listen for storage changes from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'paywallState') {
        checkPaywall();
      }
    };
    
    // Listen for tab visibility changes to refresh state
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkPaywall();
      }
    };
    
    window.addEventListener('paywallStateChanged', handlePaywallStateChange);
    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('paywallStateChanged', handlePaywallStateChange);
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkPaywall]);

  return {
    isPaywallActive,
    lastCheck
  };
}